import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment.local';

export interface SecuritySession {
  sessionId: string;
  sessionKey: string;
  issuedAt: number;
  scope: string;
}

/**
 * Guarda a sessão efêmera (sessionId + sessionKey) EM MEMÓRIA e faz o
 * bootstrap trocando o token do encrypt por uma sessão em /security/session.
 * Idempotente: chamadas repetidas com o mesmo token reaproveitam o bootstrap.
 */
@Injectable({ providedIn: 'root' })
export class CredentialService {
  private session: SecuritySession | null = null;
  private bootstrapPromise: Promise<void> | null = null;
  private bootstrappedToken: string | null = null;
  private generation = 0;
  private readonly storageKey = 'fd-simulador-ui.security.bootstrap-key';
  private readonly lifecycleKey = 'fd-simulador-ui.security.bootstrap-lifecycle';

  constructor(private http: HttpClient) {}

  /** Troca o token do encrypt por uma sessão. Chame uma vez ao entrar na jornada. */
  bootstrap(encryptToken?: string): Promise<void> {
    if (!environment.security?.enabled) {
      return Promise.resolve();
    }
    const explicitToken = !!encryptToken;
    const storedRecovery = this.readStoredRecovery();
    const storedToken = storedRecovery?.token;
    encryptToken = encryptToken || this.bootstrappedToken || storedToken || undefined;
    if (!encryptToken) {
      return Promise.resolve();
    }
    if (this.bootstrapPromise && this.bootstrappedToken === encryptToken) {
      return this.bootstrapPromise;
    }
    const generation = ++this.generation;
    const epoch = `${Date.now()}-${Math.random()}-${generation}`;
    const recoveryState = !explicitToken && storedRecovery?.token === encryptToken ? storedRecovery.state : null;
    const lifecycle = this.writeLifecycle(epoch, recoveryState === 'retryable' ? 'pending-retry' : 'pending') ? epoch : null;
    if (recoveryState !== null && lifecycle === null) {
      return Promise.resolve();
    }
    if (storedToken !== encryptToken) {
      this.removeStoredToken();
    }
    this.session = null;
    this.bootstrappedToken = encryptToken;
    this.bootstrapPromise = this.requestSession(encryptToken, generation, lifecycle, recoveryState);
    return this.bootstrapPromise;
  }

  /** Resolve quando o bootstrap em andamento terminar (ou imediatamente se não houver). */
  whenReady(): Promise<void> {
    return this.bootstrapPromise ?? Promise.resolve();
  }

  current(): SecuritySession | null {
    return this.session;
  }

  clear(): void {
    ++this.generation;
    this.session = null;
    this.bootstrapPromise = null;
    this.bootstrappedToken = null;
    if (environment.security?.enabled) {
      this.writeLifecycle(`${Date.now()}-${Math.random()}-${this.generation}`, 'invalid');
      this.removeStoredToken();
      try {
        sessionStorage.removeItem(this.lifecycleKey);
      } catch {}
    }
  }

  private async requestSession(
    encryptToken: string, generation: number, lifecycle: string | null, recoveryState: 'committed' | 'retryable' | null
  ): Promise<void> {
    const url = `${this.sessionEndpoint()}?token=${encodeURIComponent(encryptToken)}`;
    const pendingState = recoveryState === 'retryable' ? 'pending-retry' : 'pending';
    try {
      const session = await firstValueFrom(this.http.get<SecuritySession>(url));
      if (generation !== this.generation) {
        return;
      }
      const activeLifecycle = this.isActiveLifecycle(lifecycle, pendingState);
      if (activeLifecycle === false) {
        return;
      }
      this.session = session;
      if (activeLifecycle === true && lifecycle !== null) {
        try {
          sessionStorage.setItem(this.storageKey, encryptToken);
          this.writeLifecycle(lifecycle, 'committed');
        } catch {}
      }
    } catch (error) {
      const activeLifecycle = this.isActiveLifecycle(lifecycle, pendingState);
      if (generation !== this.generation || activeLifecycle === false) {
        return;
      }
      // Falha no bootstrap não pode quebrar o app: segue sem sessão (request passa sem assinatura).
      this.session = null;
      this.bootstrappedToken = null;
      if (recoveryState === 'committed' && activeLifecycle === true && lifecycle !== null && this.isTransientRecoveryError(error)) {
        try {
          if (sessionStorage.getItem(this.storageKey) === encryptToken) {
            this.writeLifecycle(lifecycle, 'retryable');
          }
        } catch {}
      }
    }
  }

  private readStoredRecovery(): { token: string; state: 'committed' | 'retryable' } | null {
    try {
      const lifecycle = JSON.parse(sessionStorage.getItem(this.lifecycleKey) || 'null');
      if (!['committed', 'retryable'].includes(lifecycle?.state) || typeof lifecycle.epoch !== 'string' || !lifecycle.epoch) {
        return null;
      }
      const token = sessionStorage.getItem(this.storageKey);
      return token ? { token, state: lifecycle.state } : null;
    } catch {
      return null;
    }
  }

  private writeLifecycle(epoch: string, state: 'pending' | 'committed' | 'invalid' | 'retryable' | 'pending-retry'): boolean {
    try {
      sessionStorage.setItem(this.lifecycleKey, JSON.stringify({ epoch, state }));
      return true;
    } catch {
      return false;
    }
  }

  private isActiveLifecycle(epoch: string | null, state: 'pending' | 'pending-retry'): boolean | null {
    if (epoch === null) {
      return null;
    }
    try {
      return sessionStorage.getItem(this.lifecycleKey) === JSON.stringify({ epoch, state });
    } catch {
      return null;
    }
  }

  private removeStoredToken(): void {
    try {
      sessionStorage.removeItem(this.storageKey);
    } catch {}
  }

  private isTransientRecoveryError(error: unknown): boolean {
    return error instanceof HttpErrorResponse && (
      [408, 502, 503, 504].includes(error.status) || (
        error.status === 0 && error.error instanceof ProgressEvent && ['error', 'timeout'].includes(error.error.type)
      )
    );
  }

  /** Deriva a raiz do contexto (apiBase/context) a partir de uma URL já configurada. */
  private sessionEndpoint(): string {
    const root = environment.apiUrls.economicGroupSimulation.replace(/\/economic-group-simulation$/, '');
    return `${root}/security/session`;
  }
}
